import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Chip, DataTable, type TableGroup } from '@/components/ui'
import type { QuoteSummary } from '../api/types'
import { QuoteActionsMenu } from './QuoteActionsMenu'
import { QuoteStatusChip } from './QuoteStatusChip'

export interface QuotesTableProps {
  rows: QuoteSummary[]
  onSelect: (q: QuoteSummary) => void
  onSend: (q: QuoteSummary) => void
  /** Optional row grouping (group-by-status / customer / salesperson). When
   *  provided, the table renders collapsible group headers; omit for flat. */
  groups?: TableGroup<QuoteSummary>[]
  collapsedGroups?: ReadonlySet<string>
  onToggleGroup?: (key: string) => void
}

function formatTotal(quote: QuoteSummary): string {
  // The summary endpoint doesn't include line totals; we display the
  // line count as a stand-in until a future serializer enrichment.
  return `${quote.line_count} line${quote.line_count === 1 ? '' : 's'}`
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString()
  } catch {
    return iso
  }
}

export function QuotesTable({
  rows, onSelect, onSend, groups, collapsedGroups, onToggleGroup,
}: QuotesTableProps) {
  const columns = useMemo<ColumnDef<QuoteSummary, unknown>[]>(
    () => [
      {
        id: 'quote_number',
        header: 'Quote #',
        accessorKey: 'quote_number',
        cell: (ctx) => (
          <span className="text-role-data-primary">
            {ctx.row.original.quote_number}
          </span>
        ),
      },
      {
        id: 'customer_id',
        header: 'Customer',
        cell: (ctx) =>
          ctx.row.original.customer_name ||
          `customer #${ctx.row.original.customer_id}`,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (ctx) => <QuoteStatusChip status={ctx.row.original.status} />,
      },
      {
        id: 'lines',
        header: 'Lines',
        cell: (ctx) => formatTotal(ctx.row.original),
      },
      {
        id: 'po',
        header: 'Customer PO',
        cell: (ctx) =>
          ctx.row.original.customer_po_number ? (
            <Chip size="xs" variant="info">
              {ctx.row.original.customer_po_number}
            </Chip>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      {
        id: 'odoo_so_id',
        header: 'Odoo SO',
        cell: (ctx) =>
          ctx.row.original.odoo_so_id ? (
            <Chip size="xs" variant="success">
              SO #{ctx.row.original.odoo_so_id}
            </Chip>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      {
        id: 'submitted_by',
        header: 'Submitted by',
        cell: (ctx) => ctx.row.original.submitted_by ?? '—',
      },
      {
        id: 'valid_through',
        header: 'Valid through',
        cell: (ctx) => formatDate(ctx.row.original.valid_through),
      },
      {
        id: 'updated_at',
        header: 'Updated',
        cell: (ctx) => formatDate(ctx.row.original.updated_at),
      },
      {
        id: 'actions',
        header: '',
        cell: (ctx) => (
          <QuoteActionsMenu
            row={ctx.row.original}
            onOpen={onSelect}
            onSend={onSend}
          />
        ),
      },
    ],
    [onSelect, onSend],
  )

  return (
    <DataTable
      data={rows}
      columns={columns}
      groups={groups}
      collapsedGroups={collapsedGroups}
      onToggleGroup={onToggleGroup}
      getRowId={(r) => String(r.id)}
      onRowClick={onSelect}
      moduleId="quotes"
      maxHeight="100%"
      columnWidths={{
        quote_number: 140,
        status: 120,
        lines: 90,
        po: 130,
        odoo_so_id: 110,
        valid_through: 130,
        updated_at: 130,
        actions: 40,
      }}
      minTableWidth={1100}
    />
  )
}
